import { evaluate } from 'mathjs';
import { Variable } from '../types';

export class CalculatorEngine {
  private variables: Variable[] = [];

  setVariables(variables: Variable[]) {
    this.variables = variables;
  }

  // Replace variables in expression with their values
  private replaceVariables(expression: string): string {
    let processedExpression = expression;
    
    // Sort variables by name length (longest first) to avoid partial replacements
    const sortedVars = [...this.variables].sort((a, b) => b.name.length - a.name.length);
    
    for (const variable of sortedVars) {
      const regex = new RegExp(`\\b${variable.name}\\b`, 'g');
      processedExpression = processedExpression.replace(regex, `(${variable.value})`);
    }
    
    return processedExpression;
  }

  // Clean and normalize expression
  private cleanExpression(expression: string): string {
    // Remove extra spaces
    let cleaned = expression.replace(/\s+/g, '');
    
    // Add multiplication signs where needed (e.g., 2(3+4) -> 2*(3+4))
    cleaned = cleaned.replace(/(\d)\(/g, '$1*(');
    cleaned = cleaned.replace(/\)(\d)/g, ')*$1');
    cleaned = cleaned.replace(/\)\(/g, ')*(');
    
    // Handle percentage (convert % to /100)
    cleaned = cleaned.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
    
    return cleaned;
  }

  // Evaluate expression safely
  evaluate(expression: string): { result: string; isError: boolean; errorMessage: string } {
    try {
      if (!expression.trim()) {
        return { result: '', isError: false, errorMessage: '' };
      }

      // Replace variables first
      const withVariables = this.replaceVariables(expression);
      
      // Clean the expression
      const cleanedExpression = this.cleanExpression(withVariables);
      
      // Evaluate using math.js
      const result = evaluate(cleanedExpression);
      
      // Format result
      let formattedResult: string;
      if (typeof result === 'number') {
        // Handle very large or very small numbers
        if (Math.abs(result) > 1e15 || (Math.abs(result) < 1e-10 && result !== 0)) {
          formattedResult = result.toExponential(6);
        } else if (Number.isInteger(result)) {
          formattedResult = result.toString();
        } else {
          // Round to 10 decimal places to avoid floating point precision issues
          formattedResult = parseFloat(result.toFixed(10)).toString();
        }
      } else if (typeof result === 'boolean') {
        formattedResult = result ? '1' : '0';
      } else {
        formattedResult = result.toString();
      }

      return { result: formattedResult, isError: false, errorMessage: '' };
    } catch (error) {
      let errorMessage = 'Invalid expression';
      
      if (error instanceof Error) {
        if (error.message.includes('Undefined symbol')) {
          errorMessage = 'Undefined variable or function';
        } else if (error.message.includes('Unexpected end of expression')) {
          errorMessage = 'Incomplete expression';
        } else if (error.message.includes('Unexpected token')) {
          errorMessage = 'Invalid syntax';
        } else if (error.message.includes('Division by zero')) {
          errorMessage = 'Division by zero';
        } else {
          errorMessage = error.message;
        }
      }
      
      return { result: '', isError: true, errorMessage };
    }
  }

  // Get available functions for autocomplete
  getAvailableFunctions(): string[] {
    return [
      'sin', 'cos', 'tan', 'asin', 'acos', 'atan',
      'sinh', 'cosh', 'tanh', 'asinh', 'acosh', 'atanh',
      'log', 'log10', 'log2', 'ln', 'exp', 'sqrt',
      'abs', 'ceil', 'floor', 'round', 'sign',
      'min', 'max', 'sum', 'mean', 'median',
      'pi', 'e', 'inf', 'NaN'
    ];
  }

  // Validate variable name
  isValidVariableName(name: string): boolean {
    // Variable names should start with a letter and contain only letters, numbers, and underscores
    return /^[a-zA-Z][a-zA-Z0-9_]*$/.test(name);
  }

  // Convert to different number bases (for programmer mode)
  convertBase(value: string, fromBase: number, toBase: number): string {
    try {
      const decimal = parseInt(value, fromBase);
      return decimal.toString(toBase).toUpperCase();
    } catch {
      return 'Error';
    }
  }

  // Get bitwise operations
  bitwiseOperation(a: string, b: string, operation: 'AND' | 'OR' | 'XOR' | 'NOT'): string {
    try {
      const numA = parseInt(a, 2);
      const numB = parseInt(b, 2);
      
      let result: number;
      switch (operation) {
        case 'AND':
          result = numA & numB;
          break;
        case 'OR':
          result = numA | numB;
          break;
        case 'XOR':
          result = numA ^ numB;
          break;
        case 'NOT':
          result = ~numA;
          break;
        default:
          return 'Error';
      }
      
      return result.toString(2);
    } catch {
      return 'Error';
    }
  }
}
